import { addDays, differenceInCalendarDays, format } from 'date-fns'
import { parseCalendarDate, toCalendarDate } from '@/lib/calendar-date'

/**
 * The portfolio calendar's horizontal axis: a run of whole days, each a
 * column. Everything on the grid is placed by its day index on this axis
 * (0 is `from`), so a pixel position is only ever `index * dayWidth` - the
 * axis does not know or care how wide a day is drawn.
 *
 * Built once per run of windows the grid spans (CAP-1416, `windows.ts`):
 * when the grid gains or drops a window, it gets a new axis.
 */
export interface AxisDay {
  /** `YYYY-MM-DD`. */
  iso: string
  /** "Wed" */
  weekday: string
  /** 28 */
  date: number
  /** "Oct" */
  month: string
  isWeekend: boolean
}

export interface DayAxis {
  /** First day, `YYYY-MM-DD`. */
  from: string
  /** Last day, inclusive. */
  to: string
  /** Local midnight of `from`. */
  start: Date
  days: AxisDay[]
  /**
   * The index of the first Monday on the axis (0-6): where the weekly
   * background pattern (weekends shaded) starts repeating.
   */
  mondayOffset: number
}

/** The axis for `from` to `to`, both inclusive. An unreadable or reversed range is an empty axis. */
export function buildDayAxis(from: string, to: string): DayAxis {
  const start = parseCalendarDate(from)
  const end = parseCalendarDate(to)
  const count = start && end ? differenceInCalendarDays(end, start) + 1 : 0
  const anchor = start ?? new Date(NaN)
  const days: AxisDay[] = []
  for (let index = 0; index < count; index++) {
    const day = addDays(anchor, index)
    const weekday = day.getDay()
    days.push({
      iso: toCalendarDate(day),
      weekday: format(day, 'EEE'),
      date: day.getDate(),
      month: format(day, 'MMM'),
      isWeekend: weekday === 0 || weekday === 6,
    })
  }
  return {
    from,
    to,
    start: anchor,
    days,
    mondayOffset: start ? (8 - start.getDay()) % 7 : 0,
  }
}

/**
 * Where a `YYYY-MM-DD` day falls on the axis, or null when it is not a real
 * date. Days before `from` are negative and days after `to` are past the end:
 * a bar that runs off the axis is clipped by its caller, not here.
 */
export function axisIndex(axis: DayAxis, day: string | Date | null | undefined): number | null {
  const date = typeof day === 'string' ? parseCalendarDate(day.slice(0, 10)) : (day ?? undefined)
  if (!date || Number.isNaN(date.getTime()) || Number.isNaN(axis.start.getTime())) return null
  return differenceInCalendarDays(date, axis.start)
}
